import { useRef, useState } from "react";
import { toast } from "sonner";
import { UploadSimple, DownloadSimple, CheckCircle, Warning } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Th, Td } from "@/components/common";
import { api, money, qty, errMsg } from "@/lib/api";

const STATUS = { new: ["Yeni", "bg-teal-100 text-teal-800"], exists: ["Zaten kayıtlı", "bg-zinc-100 text-zinc-600"], duplicate: ["Dosyada tekrar", "bg-amber-100 text-amber-800"] };

const PRESETS = {
  accounts: {
    title: "Excel'den Cari Aktar",
    desc: "Ünvan, kod, tür, e-posta, telefon, adres, vergi bilgileri ve açılış bakiyesi okunur. Zaten kayıtlı cariler atlanır.",
    base: "/accounts",
    file: "cari-sablon.xlsx",
    columns: [
      { key: "name", label: "Ünvan" },
      { key: "code", label: "Kod", num: true },
      { key: "email", label: "E-posta", small: true },
      { key: "phone", label: "Telefon", small: true },
      { key: "opening_balance", label: "Açılış", money: true },
    ],
    noun: "Cariyi",
  },
  products: {
    title: "Excel'den Ürün Aktar",
    desc: "Ürün adı, kod, kategori, birim, KDV, alış/satış fiyatı, min. stok ve açılış stoğu okunur. Zaten kayıtlı ürünler atlanır.",
    base: "/products",
    file: "urun-sablon.xlsx",
    columns: [
      { key: "name", label: "Ürün" },
      { key: "code", label: "Kod", num: true },
      { key: "unit", label: "Birim", small: true },
      { key: "purchase_price", label: "Alış", money: true },
      { key: "sale_price", label: "Satış", money: true },
      { key: "opening_stock", label: "Açılış Stoğu", qty: true },
    ],
    noun: "Ürünü",
  },
};

export default function ImportDialog({ open, onOpenChange, onImported, type = "accounts" }) {
  const cfg = PRESETS[type];
  const fileRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const [fileName, setFileName] = useState("");
  const [busy, setBusy] = useState(false);

  const reset = () => { setPreview(null); setFileName(""); };

  const pick = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setBusy(true);
    setFileName(f.name);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post(`${cfg.base}/import/preview`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(r.data);
    } catch (e2) {
      toast.error(errMsg(e2));
      reset();
    }
    setBusy(false);
  };

  const runImport = async () => {
    setBusy(true);
    try {
      const rows = preview.rows.filter((r) => r.status === "new").map(({ line, status, ...rest }) => rest);
      const r = await api.post(`${cfg.base}/import`, { rows });
      toast.success(`${r.data.created_count} kayıt eklendi${r.data.skipped_count ? `, ${r.data.skipped_count} atlandı` : ""}`);
      onImported?.();
      onOpenChange(false);
      reset();
    } catch (e) {
      toast.error(errMsg(e));
    }
    setBusy(false);
  };

  const downloadTemplate = async () => {
    try {
      const r = await api.get(`${cfg.base}/import/template`, { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(r.data);
      a.download = cfg.file;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const cellValue = (r, c) => {
    const v = r[c.key];
    if (c.money) return v ? money(v) : "—";
    if (c.qty) return v ? qty(v) : "—";
    return v || "—";
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) reset(); }}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-4xl" data-testid="import-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><UploadSimple size={18} /> {cfg.title}</DialogTitle>
          <DialogDescription>{cfg.desc}</DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" className="rounded-[4px]" onClick={downloadTemplate} data-testid="import-template-button"><DownloadSimple /> Şablonu İndir</Button>
          <Button className="rounded-[4px]" disabled={busy} onClick={() => fileRef.current?.click()} data-testid="import-pick-file-button">
            <UploadSimple /> {busy && !preview ? "Okunuyor…" : "Dosya Seç (.xlsx / .csv)"}
          </Button>
          <input ref={fileRef} type="file" accept=".xlsx,.xlsm,.csv" className="hidden" onChange={pick} data-testid="import-file-input" />
          {fileName && <span className="text-xs text-muted-foreground" data-testid="import-file-name">{fileName}</span>}
        </div>

        {preview && (
          <div className="mt-2 min-w-0 space-y-3">
            <div className="flex flex-wrap gap-4 border border-border p-3 text-sm" data-testid="import-summary">
              <span><b>{preview.total}</b> satır okundu</span>
              <span className="text-teal-700"><b>{preview.new}</b> yeni kayıt eklenecek</span>
              <span className="text-muted-foreground"><b>{preview.skipped}</b> atlanacak</span>
            </div>
            <div className="max-h-[45vh] min-w-0 overflow-auto border border-border">
              <table className="w-full min-w-[680px]">
                <thead><tr><Th>Satır</Th>{cfg.columns.map((c) => <Th key={c.key} right={!!(c.money || c.qty)}>{c.label}</Th>)}<Th>Durum</Th></tr></thead>
                <tbody>
                  {preview.rows.map((r) => (
                    <tr key={r.line} data-testid={`import-row-${r.line}`}>
                      <Td className="font-num">{r.line}</Td>
                      {cfg.columns.map((c) => (
                        <Td key={c.key} right={!!(c.money || c.qty)} title={String(r[c.key] ?? "")} className={`${c.num ? "font-num" : ""} ${c.small ? "text-xs" : ""} max-w-[260px] truncate`}>{cellValue(r, c)}</Td>
                      ))}
                      <Td><span className={`inline-block whitespace-nowrap px-2 py-0.5 text-[11px] font-semibold ${STATUS[r.status][1]}`}>{STATUS[r.status][0]}</span></Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {preview.warnings?.length > 0 && (
              <div className="flex gap-2 border border-amber-300 bg-amber-50 p-3 text-xs text-amber-800" data-testid="import-warnings">
                <Warning size={16} className="shrink-0" />
                <ul className="space-y-1">{preview.warnings.map((w) => <li key={w}>{w}</li>)}</ul>
              </div>
            )}
            <Button onClick={runImport} disabled={busy || preview.new === 0} className="w-full rounded-[4px]" data-testid="import-confirm-button">
              <CheckCircle /> {busy ? "Aktarılıyor…" : `${preview.new} ${cfg.noun} Aktar`}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
